import { useState, useCallback, useEffect, useMemo } from "react";
import { ColumnConfig, DEFAULT_COLUMNS } from "../types";
import { LOCAL_STORAGE_BASE_KEYS } from "@/types/v2/LocalStorage";
import { localStorageManager } from "@/services/localStorage";

interface ColumnState {
  columns: ColumnConfig[];
  hiddenColumns: string[];
  columnOrder: string[];
}

interface UsePersistentColumnsParams {
  shopId?: string;
}

interface UsePersistentColumnsReturn {
  columns: ColumnConfig[];
  visibleColumns: ColumnConfig[];
  hiddenColumnIds: string[];
  columnOrder: string[];
  updateColumnVisibility: (columnId: string, visible: boolean) => void;
  updateColumnWidth: (columnId: string, width: number) => void;
  reorderColumns: (newOrder: string[]) => void;
  resetToDefaults: () => void;
  saveColumnState: () => void;
}

const DEFAULT_COLUMN_STATE: ColumnState = {
  columns: [...DEFAULT_COLUMNS],
  hiddenColumns: [],
  columnOrder: DEFAULT_COLUMNS.map((col) => col.id),
};

export const usePersistentColumns = ({
  shopId,
}: UsePersistentColumnsParams): UsePersistentColumnsReturn => {
  // Load initial state from localStorage or use defaults
  const loadColumnState = useCallback((): ColumnState => {
    try {
      const saved = localStorageManager.getItem(
        LOCAL_STORAGE_BASE_KEYS.DASHBOARD_LIST_COLUMNS,
        shopId
      );
      if (saved) {
        const parsed = JSON.parse(saved) as ColumnState;

        // Merge with defaults to handle new columns or schema changes
        const mergedColumns = DEFAULT_COLUMNS.map((defaultCol) => {
          const savedCol = parsed.columns.find(
            (col) => col.id === defaultCol.id
          );
          return savedCol ? { ...defaultCol, ...savedCol } : defaultCol;
        });

        return {
          columns: mergedColumns,
          hiddenColumns: parsed.hiddenColumns || [],
          columnOrder:
            parsed.columnOrder || DEFAULT_COLUMNS.map((col) => col.id),
        };
      }
    } catch (error) {
      console.warn("Failed to load column state from localStorage:", error);
    }
    return DEFAULT_COLUMN_STATE;
  }, [shopId]);

  const [columnState, setColumnState] = useState<ColumnState>(loadColumnState);

  // Save to localStorage
  const saveColumnState = useCallback(() => {
    try {
      localStorageManager.setItem(
        LOCAL_STORAGE_BASE_KEYS.DASHBOARD_LIST_COLUMNS,
        JSON.stringify(columnState),
        shopId
      );
    } catch (error) {
      console.warn("Failed to save column state to localStorage:", error);
    }
  }, [shopId, columnState]);

  // Auto-save when state changes
  useEffect(() => {
    saveColumnState();
  }, [saveColumnState]);

  // Compute visible columns based on current state
  const visibleColumns = useMemo(() => {
    const orderedColumns = columnState.columnOrder
      .map((id) => columnState.columns.find((col) => col.id === id))
      .filter((col): col is ColumnConfig => col !== undefined);

    return orderedColumns.filter(
      (col) => col.visible && !columnState.hiddenColumns.includes(col.id)
    );
  }, [columnState]);

  // Update column visibility
  const updateColumnVisibility = useCallback(
    (columnId: string, visible: boolean) => {
      setColumnState((prev) => {
        const updatedColumns = prev.columns.map((col) =>
          col.id === columnId ? { ...col, visible } : col
        );

        const updatedHiddenColumns = visible
          ? prev.hiddenColumns.filter((id) => id !== columnId)
          : prev.hiddenColumns.includes(columnId)
            ? prev.hiddenColumns
            : [...prev.hiddenColumns, columnId];

        return {
          ...prev,
          columns: updatedColumns,
          hiddenColumns: updatedHiddenColumns,
        };
      });
    },
    []
  );

  // Update column width
  const updateColumnWidth = useCallback((columnId: string, width: number) => {
    setColumnState((prev) => ({
      ...prev,
      columns: prev.columns.map((col) =>
        col.id === columnId
          ? { ...col, width: Math.max(width, col.minWidth) }
          : col
      ),
    }));
  }, []);

  // Reorder columns
  const reorderColumns = useCallback((newOrder: string[]) => {
    setColumnState((prev) => ({
      ...prev,
      columnOrder: newOrder,
    }));
  }, []);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    setColumnState(DEFAULT_COLUMN_STATE);
  }, []);

  return {
    columns: columnState.columns,
    visibleColumns,
    hiddenColumnIds: columnState.hiddenColumns,
    columnOrder: columnState.columnOrder,
    updateColumnVisibility,
    updateColumnWidth,
    reorderColumns,
    resetToDefaults,
    saveColumnState,
  };
};
